import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCheck, FiX, FiAlertCircle, FiClock, FiCheckCircle,
  FiSave, FiSend, FiCheckSquare, FiUser, FiPrinter
} from 'react-icons/fi';
import { getQCChecklist, saveQCChecklist, approveQCChecklist, getAuthToken } from '../services/api';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const TYPE_LABELS = {
  boundary: 'Boundary / Deed Survey',
  topo: 'Topographic Survey',
  construction_staking: 'Construction Staking',
  alta: 'ALTA/NSPS Survey',
  subdivision: 'Subdivision Plat',
};

const STATUS_CONFIG = {
  in_progress: { label: 'In Progress', bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: FiClock },
  revisions_required: { label: 'Revisions Required', bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: FiAlertCircle },
  approved: { label: 'Approved', bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', icon: FiCheckCircle },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
  const Icon = config.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 12px', borderRadius: '14px', fontSize: '13px', fontWeight: '600',
      backgroundColor: config.bg, color: config.color, border: `1px solid ${config.border}`,
    }}>
      <Icon size={14} /> {config.label}
    </span>
  );
}

function QCReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManagePayroll = user?.can_manage_payroll || false;

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [notes, setNotes] = useState('');
  const [reviewedBy, setReviewedBy] = useState('');
  const [draftedBy, setDraftedBy] = useState('');
  // Map of itemId -> { result, comment }
  const [itemEdits, setItemEdits] = useState({});

  const saveTimer = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const loadChecklist = useCallback(async () => {
    try {
      const res = await getQCChecklist(id);
      setChecklist(res.data);
      setNotes(res.data.notes || '');
      setReviewedBy(res.data.reviewed_by || '');
      setDraftedBy(res.data.drafted_by || '');
      // Seed item edits from server data so local state stays in sync
      const edits = {};
      Object.values(res.data.categories || {}).forEach(items => {
        items.forEach(item => {
          edits[item.id] = { result: item.result, comment: item.comment || '' };
        });
      });
      setItemEdits(edits);
    } catch (error) {
      console.error('Error loading checklist:', error);
      alert('Failed to load checklist');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  // Compute live totals from local edits (so the UI updates without waiting for the server)
  const totals = React.useMemo(() => {
    const counts = { total: 0, pass: 0, fail: 0, unchecked: 0 };
    Object.values(itemEdits).forEach(e => {
      counts.total += 1;
      if (e.result === 'pass') counts.pass += 1;
      else if (e.result === 'fail') counts.fail += 1;
      else counts.unchecked += 1;
    });
    return counts;
  }, [itemEdits]);

  const allChecked = totals.total > 0 && totals.unchecked === 0;
  const hasFailures = totals.fail > 0;
  const canApprove = allChecked && !hasFailures && canManagePayroll;
  const progressPercent = totals.total > 0 ? Math.round(((totals.pass + totals.fail) / totals.total) * 100) : 0;

  // Build the items array to send to the server
  const buildItemsPayload = useCallback(() => {
    return Object.entries(itemEdits).map(([itemId, edit]) => ({
      id: parseInt(itemId, 10),
      result: edit.result,
      comment: edit.comment || null,
    }));
  }, [itemEdits]);

  // Auto-save (debounced) — fires 1.5s after the last change
  const scheduleAutoSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await saveQCChecklist(id, {
          items: buildItemsPayload(),
          notes,
          reviewed_by: reviewedBy,
          drafted_by: draftedBy,
        });
        setSavedAt(new Date());
      } catch (e) {
        console.error('Auto-save failed:', e);
      }
      setSaving(false);
    }, 1500);
  }, [id, buildItemsPayload, notes, reviewedBy, draftedBy]);

  useEffect(() => {
    if (!loading && checklist) scheduleAutoSave();
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemEdits, notes, reviewedBy, draftedBy]);

  const handleResultChange = (itemId, newResult) => {
    setItemEdits(prev => ({
      ...prev,
      [itemId]: {
        result: newResult,
        // Clear comment if going from fail back to pass/unchecked
        comment: newResult === 'fail' ? (prev[itemId]?.comment || '') : '',
      },
    }));
  };

  const handleCommentChange = (itemId, comment) => {
    setItemEdits(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], comment },
    }));
  };

  const handlePrint = async () => {
    // Save current state first so the print view reflects unsaved edits
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (!isReadOnly) {
      try {
        setSaving(true);
        await saveQCChecklist(id, {
          items: buildItemsPayload(),
          notes,
          reviewed_by: reviewedBy,
          drafted_by: draftedBy,
        });
        setSavedAt(new Date());
      } catch (e) {
        // Don't block printing if save fails — just warn
        console.warn('Save before print failed:', e);
      } finally {
        setSaving(false);
      }
    }
    // Open print view in a new tab; pass token via query string
    const token = await getAuthToken();
    const url = `${API_URL}/qc-checklists/${id}/print${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSaveProgress = async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaving(true);
    try {
      const res = await saveQCChecklist(id, {
        items: buildItemsPayload(),
        notes,
        reviewed_by: reviewedBy,
        drafted_by: draftedBy,
      });
      setChecklist(res.data);
      setSavedAt(new Date());
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  const handleMarkRevisions = async () => {
    if (!window.confirm('Mark this checklist as Revisions Required? The drafter will need to address the failed items.')) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaving(true);
    try {
      const res = await saveQCChecklist(id, {
        items: buildItemsPayload(),
        notes,
        reviewed_by: reviewedBy,
        drafted_by: draftedBy,
        status: 'revisions_required',
      });
      setChecklist(res.data);
      setSavedAt(new Date());
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update');
    }
    setSaving(false);
  };

  const handleApprove = async () => {
    if (!canApprove) return;
    if (!window.confirm('Approve this checklist? This marks the work as ready for surveyor sign-off.')) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaving(true);
    try {
      // Save current edits first, then approve
      await saveQCChecklist(id, {
        items: buildItemsPayload(),
        notes,
        reviewed_by: reviewedBy,
        drafted_by: draftedBy,
      });
      const res = await approveQCChecklist(id);
      setChecklist(res.data);
      setSavedAt(new Date());
      alert('Checklist approved!');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to approve');
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading checklist…</div>;
  }
  if (!checklist) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>Checklist not found.</div>;
  }

  const isReadOnly = checklist.status === 'approved';

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '1100px', margin: '0 auto', paddingBottom: '120px' }}>
      {/* Back link */}
      <Link
        to="/qc"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          color: '#6b7280', textDecoration: 'none', fontSize: '14px', marginBottom: '12px',
        }}
      >
        <FiArrowLeft size={14} /> Back to QC Dashboard
      </Link>

      {/* Header card */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: '12px', flexWrap: 'wrap', marginBottom: '12px',
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 style={{ margin: '0 0 4px', fontSize: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiCheckSquare /> {checklist.job_number || `Job #${checklist.job_id}`}
            </h1>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              {TYPE_LABELS[checklist.checklist_type] || checklist.checklist_type}
            </div>
          </div>
          <StatusBadge status={checklist.status} />
        </div>

        {/* Drafted By / Reviewed By */}
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '12px', marginBottom: '12px',
        }}>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Drafted By
            </label>
            <input
              type="text"
              value={draftedBy}
              onChange={(e) => setDraftedBy(e.target.value)}
              disabled={isReadOnly}
              placeholder="Drafter's name"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', marginTop: '4px', boxSizing: 'border-box',
                backgroundColor: isReadOnly ? '#f9fafb' : 'white',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Reviewed By
            </label>
            <input
              type="text"
              value={reviewedBy}
              onChange={(e) => setReviewedBy(e.target.value)}
              disabled={isReadOnly}
              placeholder="Reviewer's name"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', marginTop: '4px', boxSizing: 'border-box',
                backgroundColor: isReadOnly ? '#f9fafb' : 'white',
              }}
            />
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '13px', color: '#374151', marginBottom: '6px',
          }}>
            <span style={{ fontWeight: '600' }}>
              Progress: {totals.pass + totals.fail} of {totals.total} items
              {totals.fail > 0 && (
                <span style={{ color: '#dc2626', marginLeft: '10px' }}>
                  · {totals.fail} failed
                </span>
              )}
            </span>
            <span style={{ color: '#6b7280' }}>{progressPercent}%</span>
          </div>
          <div style={{
            height: '10px', backgroundColor: '#e5e7eb', borderRadius: '5px', overflow: 'hidden',
            display: 'flex',
          }}>
            <div style={{
              width: `${totals.total > 0 ? (totals.pass / totals.total) * 100 : 0}%`,
              backgroundColor: '#16a34a', transition: 'width 0.2s',
            }} />
            <div style={{
              width: `${totals.total > 0 ? (totals.fail / totals.total) * 100 : 0}%`,
              backgroundColor: '#dc2626', transition: 'width 0.2s',
            }} />
          </div>
        </div>
      </div>

      {/* Items grouped by category */}
      {Object.entries(checklist.categories || {}).map(([category, items]) => (
        <div
          key={category}
          style={{
            backgroundColor: 'white', borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '12px', overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb',
            fontSize: '14px', fontWeight: '600', color: '#374151',
          }}>
            {category}
          </div>
          <div>
            {items.map(item => {
              const edit = itemEdits[item.id] || { result: 'unchecked', comment: '' };
              const isPass = edit.result === 'pass';
              const isFail = edit.result === 'fail';
              return (
                <div
                  key={item.id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: isFail ? '#fef2f2' : isPass ? '#f0fdf4' : 'white',
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}>
                    <div style={{ flex: 1, fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>
                      {item.item_text}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => !isReadOnly && handleResultChange(item.id, isPass ? 'unchecked' : 'pass')}
                        disabled={isReadOnly}
                        style={{
                          padding: '8px 14px', minWidth: '70px',
                          borderRadius: '8px', cursor: isReadOnly ? 'default' : 'pointer',
                          fontSize: '13px', fontWeight: '600',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                          border: '2px solid',
                          borderColor: isPass ? '#16a34a' : '#d1d5db',
                          backgroundColor: isPass ? '#16a34a' : 'white',
                          color: isPass ? 'white' : '#6b7280',
                          opacity: isReadOnly ? 0.7 : 1,
                        }}
                      >
                        <FiCheck size={14} /> Pass
                      </button>
                      <button
                        onClick={() => !isReadOnly && handleResultChange(item.id, isFail ? 'unchecked' : 'fail')}
                        disabled={isReadOnly}
                        style={{
                          padding: '8px 14px', minWidth: '70px',
                          borderRadius: '8px', cursor: isReadOnly ? 'default' : 'pointer',
                          fontSize: '13px', fontWeight: '600',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                          border: '2px solid',
                          borderColor: isFail ? '#dc2626' : '#d1d5db',
                          backgroundColor: isFail ? '#dc2626' : 'white',
                          color: isFail ? 'white' : '#6b7280',
                          opacity: isReadOnly ? 0.7 : 1,
                        }}
                      >
                        <FiX size={14} /> Fail
                      </button>
                    </div>
                  </div>
                  {isFail && (
                    <div style={{ marginTop: '10px' }}>
                      <textarea
                        value={edit.comment || ''}
                        onChange={(e) => handleCommentChange(item.id, e.target.value)}
                        placeholder="Describe what needs to be corrected..."
                        disabled={isReadOnly}
                        rows={2}
                        style={{
                          width: '100%', padding: '8px 12px', border: '1px solid #fca5a5',
                          borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                          fontFamily: 'inherit', resize: 'vertical',
                          backgroundColor: 'white',
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* General notes */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '16px',
      }}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>
          General Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isReadOnly}
          rows={4}
          placeholder="Any additional notes for the drafter or surveyor..."
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
            borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
            fontFamily: 'inherit', resize: 'vertical',
            backgroundColor: isReadOnly ? '#f9fafb' : 'white',
          }}
        />
      </div>

      {/* Sticky action bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: 'white', borderTop: '1px solid #e5e7eb',
        padding: isMobile ? '12px' : '14px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '8px', flexWrap: 'wrap', boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
        zIndex: 100,
      }}>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {saving
            ? 'Saving…'
            : savedAt
              ? `Saved ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              : 'Auto-save enabled'}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handlePrint}
            disabled={saving}
            title="Open printable version in a new tab"
            style={{
              padding: '10px 16px', backgroundColor: 'white', border: '1px solid #d1d5db',
              borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '600', color: '#374151',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <FiPrinter size={14} /> Print Checklist
          </button>
          {!isReadOnly && (
            <>
              <button
                onClick={handleSaveProgress}
                disabled={saving}
                style={{
                  padding: '10px 16px', backgroundColor: 'white', border: '1px solid #d1d5db',
                  borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '600', color: '#374151',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiSave size={14} /> Save Progress
              </button>
              <button
                onClick={handleMarkRevisions}
                disabled={saving}
                style={{
                  padding: '10px 16px', backgroundColor: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiAlertCircle size={14} /> Revisions Required
              </button>
              <button
                onClick={handleApprove}
                disabled={!canApprove || saving}
                title={
                  !canManagePayroll
                    ? 'Only surveyors can approve checklists'
                    : !allChecked
                      ? `${totals.unchecked} item(s) still unchecked`
                      : hasFailures
                        ? `${totals.fail} item(s) failed — must be revised first`
                        : 'Approve this checklist'
                }
                style={{
                  padding: '10px 16px',
                  backgroundColor: canApprove ? '#16a34a' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: (canApprove && !saving) ? 'pointer' : 'not-allowed',
                  fontSize: '13px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FiCheckCircle size={14} /> Approve
              </button>
            </>
          )}
          {isReadOnly && (
            <div style={{ fontSize: '13px', color: '#16a34a', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FiCheckCircle size={16} /> Approved {checklist.surveyor_approval_date ? `on ${new Date(checklist.surveyor_approval_date).toLocaleDateString()}` : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QCReview;
